import { Component, Inject, NgZone, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { SharedModule } from './shared/shared.module';
import { HomeComponent } from './static-pages/home/home.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LANGUAGES } from './core/model/constants/languages.costant';
import { CommonService } from './core/services/common.service';
import { getCountryCulture } from './store/actions/shared.action';
import { filter } from 'rxjs';
import { cultureSelctor} from './store/selectors/shared.selector';
import { isPlatformBrowser } from '@angular/common';
import { SwService } from './services/sw.service';
import { userApi } from './core/model/constants/endpoints';
import { environment } from '../environments/environment';
import { FestivalThemeService } from './core/festival/festival-theme.service';
declare const window: any;
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [SharedModule, TranslateModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'Opinion-Edge';
  private availableLanguages = LANGUAGES;
  constructor(
    private router: Router,
    private commonService: CommonService,
    private ngZone: NgZone,
    private swService: SwService,
    private festivalService: FestivalThemeService,
    @Inject(PLATFORM_ID) private plateformId: any,
  ) {
    this.commonService.isLoadApp.next(true)
    this.commonService.translate.addLangs(['de', 'en']);
    const browserLang = 'en';
    this.commonService.translate.use(browserLang)
  }
  private verisoulLoadPromise: Promise<void> | null = null;
  panelistId: boolean;
  countryCode: string;
  ngOnInit() {
    // this.isShowDownTime();
    this.initializeSecurity();     // Security code for prevent clickJacking
    this.isLoadApplication();
    this.handleGetCulture();
     this.festivalService.init();
  }

  private loadVerisoulScript(): Promise<void> {
    if (this.verisoulLoadPromise) {
      return this.verisoulLoadPromise;
    }

    this.verisoulLoadPromise = new Promise((resolve, reject) => {
      if (document.getElementById('verisoul-sdk') && window.Verisoul) {
        resolve();
        return;
      }
  
      const script = document.createElement('script');
      script.id = 'verisoul-sdk';
      script.src = environment.verisoulScriptUrl;
      script.async = true;
  
      script.setAttribute('verisoul-project-id', environment.varisoulId);

      script.onload = () => resolve();
      script.onerror = (err) => {
          script.remove();
          this.verisoulLoadPromise = null; // cache clear — next call phir try karega
          reject(err);
      };

      document.head.appendChild(script);
    });

    return this.verisoulLoadPromise;
  }

  switchLanguage(): void {
    this.commonService.store.select(cultureSelctor).pipe(
      filter((res: any) => !!res?.culture)
    ).subscribe((res: any) => {
      const getCountryCulture = res?.culture?.split('-')[0];
      this.commonService.translate.use(getCountryCulture);
      this.countryCode = res?.culture?.split('-')[1]?.toUpperCase();
    });
  }

  handleGetCulture() {
    this.commonService.store.dispatch(getCountryCulture.loadCountryCulture({ status: 'request' }))
    this.switchLanguage();
  }

  getAvailableLanguages(): string[] {
    return this.availableLanguages;
  }

  isLoadApplication() {
    this.commonService.isLoadApp.subscribe((res) => {
      const splash = document.getElementById('splash-screen');
      if (res) {
        splash.style.display = 'none';
      }
    })
  }

  addDynamicRoute(): void {
    const newRoute = { path: '', component: HomeComponent };

    // Add the new route to the existing routes
    const currentRoutes = this.router.config;
    this.router.resetConfig([...currentRoutes, newRoute]);

    // Navigate to the new dynamic route
    this.router.navigate(['/']);
  }

  startMonitoring() {
    if (isPlatformBrowser(this.plateformId)) {
      window.addEventListener('offline', () => {
        this.ngZone.run(() => {
          if (this.router.url !== '/serverDown') {
            this.router.navigate(['/serverDown']);
          }
        });
      });


      window.addEventListener('online', () => {
        this.ngZone.run(() => {
          this.router.navigate(['/']); // or some other page
        });
      });
    }

  }

  handleUserMessage(event) {
    // Handle the user message event here
  }

 async  initializeSecurity() {
    // Check if page is loaded in iframe
    if (this.isInIframe()) {
      console.warn('🚨 Clickjacking attempt detected and blocked!');
      this.handleFrameDetection();
    }
    await this.loadVerisoulScript(); // Load Verisoul script
  }
  
  private isInIframe(): boolean {
    try {
      // Multiple detection methods
      return window.self !== window.top || 
             window.frameElement !== null ||
             window.location !== window.parent.location;
    } catch (e) {
      // If we can't access parent, we're likely in a cross-origin iframe
      return true;
    }
  }
  
  private handleFrameDetection(): void {
    // 1. Try silent navigation (will fail silently if cross-origin)
    this.attemptFrameBreaking();
    
    // 2. Block content display
    this.blockMaliciousContent();
    
    // 3. Show user warning
    this.displaySecurityWarning();
  }
  
  private attemptFrameBreaking(): void {
    try {
      // Method 1: Try to access parent location
      if (window.parent && window.parent !== window) {
        // This will fail silently for cross-origin
        setTimeout(() => {
          try {
            window.parent.location.href = window.location.href;
          } catch (navError) {
            // Expected error - browser blocked cross-origin navigation
            console.log('✅ Browser security prevented cross-origin navigation (good!)');
          }
        }, 100);
      }
      
      // Method 2: Try top window navigation
      try {
        if (window.top && window.top !== window) {
          window.top.location.replace(window.location.href);
        }
      } catch (topError) {
        // Expected error - cross-origin blocked
        console.log('✅ Cross-origin protection active');
      }
      
    } catch (error) {
      // All navigation attempts blocked - this is good for security
      console.log('✅ Frame breaking completed - user protected');
    }
  }
  
  private blockMaliciousContent(): void {
    // Hide original content immediately
    const appRoot = document.querySelector('app-root');
    if (appRoot) {
      (appRoot as HTMLElement).style.display = 'none';
    }
    
    // Also hide body content
    document.body.style.background = '#f8f9fa';
  }
  
  private displaySecurityWarning(): void {
    // Create professional security warning
    const warningOverlay = this.createWarningOverlay();
    document.body.appendChild(warningOverlay);
  }
  
  private createWarningOverlay(): any {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.98);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    
    overlay.innerHTML = `
      <div style="
        max-width: 500px;
        padding: 40px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        text-align: center;
        border: 1px solid #e9ecef;
      ">
        <div style="
          width: 60px;
          height: 60px;
          margin: 0 auto 20px;
          background: #dc3545;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
        ">🛡️</div>
        
        <h2 style="
          color: #212529;
          margin-bottom: 16px;
          font-size: 24px;
          font-weight: 600;
        ">Security Protection Active</h2>
        
        <p style="
          color: #6c757d;
          font-size: 16px;
          line-height: 1.5;
          margin-bottom: 24px;
        ">
          This application detected it's being loaded in a frame, which could be a security risk.
          For your protection, please access it directly.
        </p>
        
        <div style="
          background: #f8f9fa;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 24px;
          font-size: 14px;
          color: #495057;
        ">
          <strong>Direct URL:</strong><br>
          ${window.location.origin}
        </div>
        
        <button onclick="window.open('${window.location.href}', '_blank', 'noopener,noreferrer')" 
                style="
                  background: #007bff;
                  color: white;
                  border: none;
                  padding: 12px 24px;
                  border-radius: 6px;
                  font-size: 16px;
                  font-weight: 500;
                  cursor: pointer;
                  transition: background-color 0.2s;
                "
                onmouseover="this.style.background='#0056b3'"
                onmouseout="this.style.background='#007bff'">
          Open in New Tab
        </button>
        
        <p style="
          font-size: 12px;
          color: #adb5bd;
          margin-top: 20px;
          margin-bottom: 0;
        ">
          This security measure protects against clickjacking attacks
        </p>
      </div>
    `;
    
    return overlay;
}


  isShowDownTime() {
    this.commonService.httpApiService.get(userApi.downtimeAlert).then((res: any) => {
      const showPopUp = res?.maintenance === "true" ? true : false;
      if (showPopUp) {
        this.commonService.router.navigate(['downTime'])
      }
    }).catch((err: any) => {
    })
  }
}
